/**
 * NavigationContext for UI-Model-1 (API-Direct Approach)
 *
 * Thin wrapper context that:
 * - Reads navigation data from UIModelStateContext (state.model.layout.navigation)
 * - Dispatches commands for mutations via UIModelStateContext
 * - Provides domain-specific convenience methods
 *
 * Line limit: ~200 lines
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import {
  GroupAPI,
  PageAPI,
  CanvasAPI,
  NavigationConfigAPI,
  DEFAULT_NAVIGATION_CONFIGURATION,
  createGroup,
  createPage,
  createCanvas,
} from '../types/state.types';
import { NavigationContextValue } from '../types/context.types';
import { useUIModelState } from './UIModelStateContext';
import { useUIModelApi } from './UIModelApiContext';
import { useSelection } from './SelectionContext';
import { generateId } from '../utils/idGenerator';

// ============================================================================
// Context
// ============================================================================

const NavigationContext = createContext<NavigationContextValue | null>(null);

export const NavigationProvider = ({ children }: { children: React.ReactNode }) => {
  const { state, dispatch, getGroup, getPage, getPagesForGroup } = useUIModelState();
  const { deleteCanvas } = useUIModelApi();
  const { activeGroupId, activePageId, setActivePage } = useSelection();

  // ============================================================================
  // Derived State (from UIModelState)
  // ============================================================================

  const groups = useMemo<GroupAPI[]>(() => {
    if (!state.model?.layout?.navigation?.groups) return [];
    return [...state.model.layout.navigation.groups].sort((a, b) => a.order - b.order);
  }, [state.model?.layout?.navigation?.groups]);

  const configuration = useMemo<NavigationConfigAPI>(() => {
    if (!state.model?.layout?.navigation?.configuration) {
      return DEFAULT_NAVIGATION_CONFIGURATION;
    }
    return state.model.layout.navigation.configuration;
  }, [state.model?.layout?.navigation?.configuration]);

  const activeCanvasId = useMemo(() => {
    if (!activeGroupId || !activePageId) return null;
    return `${activeGroupId}_${activePageId}`;
  }, [activeGroupId, activePageId]);

  // ============================================================================
  // Internal Helpers — reduce nested-spread + dual-dispatch boilerplate
  // ============================================================================

  /** Dispatch a navigation model update with updated groups and optional canvas override */
  const dispatchGroupsUpdate = useCallback((
    newGroups: GroupAPI[],
    canvas?: Record<string, CanvasAPI>
  ) => {
    if (!state.model) return;
    const newModel = {
      ...state.model,
      layout: {
        ...state.model.layout,
        navigation: { ...state.model.layout.navigation, groups: newGroups },
        ...(canvas !== undefined && { canvas }),
      },
    };
    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });
  }, [state.model, dispatch]);

  /** Pick the neighbor index to auto-select after deleting an item from a sorted list */
  const pickNeighborIndex = (deletedIndex: number, remainingLength: number): number =>
    Math.min(Math.max(deletedIndex - 1, 0), remainingLength - 1);

  // ============================================================================
  // Group Operations (dispatch commands)
  // ============================================================================

  const addGroup = useCallback((name: string): string => {
    if (!state.model) return '';

    const id = generateId('group');
    const order = groups.length;
    const newGroup = createGroup(id, name, order);

    dispatchGroupsUpdate([...(state.model.layout.navigation.groups || []), newGroup]);

    return id;
  }, [state.model, groups.length, dispatchGroupsUpdate]);

  const removeGroup = useCallback((groupId: string) => {
    if (!state.model?.layout?.navigation?.groups) return;

    const newGroups = state.model.layout.navigation.groups.filter(g => g.id !== groupId);
    // Also remove associated canvases
    const newCanvas = { ...state.model.layout.canvas };
    const group = state.model.layout.navigation.groups.find(g => g.id === groupId);
    if (group) {
      group.pages.forEach(page => {
        const canvasId = page.canvasId;
        delete newCanvas[canvasId];
        // Fire-and-forget delete API call
        deleteCanvas(canvasId, state.model?.id).catch(err => console.error('Failed to delete canvas:', err));
      });
    }

    dispatchGroupsUpdate(newGroups, newCanvas);

    // Auto-select previous group (or next if first was deleted)
    const sortedRemaining = newGroups.sort((a, b) => a.order - b.order);
    if (sortedRemaining.length > 0) {
      const originalSorted = [...state.model.layout.navigation.groups].sort((a, b) => a.order - b.order);
      const deletedIndex = originalSorted.findIndex(g => g.id === groupId);
      const targetIndex = pickNeighborIndex(deletedIndex, sortedRemaining.length);
      const targetGroup = sortedRemaining[targetIndex];
      const firstPage = [...targetGroup.pages].sort((a, b) => a.order - b.order)[0];
      if (firstPage) {
        dispatch({ type: 'SET_ACTIVE_GROUP', payload: { groupId: targetGroup.id } });
        dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId: firstPage.id } });
      }
    }
  }, [state.model, dispatch, deleteCanvas, dispatchGroupsUpdate]);

  const updateGroup = useCallback((groupId: string, updates: Partial<GroupAPI>) => {
    if (!state.model?.layout?.navigation?.groups) return;

    const newGroups = state.model.layout.navigation.groups.map(g =>
      g.id === groupId ? { ...g, ...updates } : g
    );

    dispatchGroupsUpdate(newGroups);
  }, [state.model, dispatchGroupsUpdate]);

  const reorderGroups = useCallback((orderedIds: string[]) => {
    if (!state.model?.layout?.navigation?.groups) return;

    const newGroups = orderedIds
      .map((id, index) => {
        const group = state.model!.layout.navigation.groups.find(g => g.id === id);
        return group ? { ...group, order: index } : null;
      })
      .filter((g): g is GroupAPI => g !== null);

    dispatchGroupsUpdate(newGroups);
  }, [state.model, dispatchGroupsUpdate]);

  // ============================================================================
  // Page Operations
  // ============================================================================

  const addPage = useCallback((groupId: string, name: string): string => {
    if (!state.model?.layout?.navigation?.groups) return '';

    const group = state.model.layout.navigation.groups.find(g => g.id === groupId);
    if (!group) return '';

    const id = generateId('page');
    const canvasId = `${groupId}_${id}`;
    const order = group.pages.length;
    const newPage = createPage(id, name, canvasId, order);
    const newCanvas = createCanvas(id, groupId);

    // Update group with new page
    const newGroups = state.model.layout.navigation.groups.map(g =>
      g.id === groupId ? { ...g, pages: [...g.pages, newPage] } : g
    );

    dispatchGroupsUpdate(newGroups, { ...state.model.layout.canvas, [canvasId]: newCanvas });

    // Mark the new canvas dirty so it gets saved to the API
    // (dispatchGroupsUpdate only marks 'navigation' dirty, not the new canvas entry)
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'canvas' as const, canvasId } });

    return id;
  }, [state.model, dispatch, dispatchGroupsUpdate]);

  const removePage = useCallback((groupId: string, pageId: string) => {
    if (!state.model?.layout?.navigation?.groups) return;

    // Get canvasId from the page object (supports old UI-Model UUID canvasIds)
    const group = state.model.layout.navigation.groups.find(g => g.id === groupId);
    const page = group?.pages.find(p => p.id === pageId);
    const canvasId = page?.canvasId || `${groupId}_${pageId}`;

    // Fire-and-forget delete API call
    deleteCanvas(canvasId, state.model?.id).catch(err => console.error('Failed to delete canvas:', err));

    // Update group without the page
    const newGroups = state.model.layout.navigation.groups.map(g =>
      g.id === groupId
        ? { ...g, pages: g.pages.filter(p => p.id !== pageId) }
        : g
    );

    // Remove the canvas
    const newCanvas = { ...state.model.layout.canvas };
    delete newCanvas[canvasId];

    dispatchGroupsUpdate(newGroups, newCanvas);

    // Auto-select previous page (or next if first was deleted)
    const remainingPages = newGroups
      .find(g => g.id === groupId)?.pages
      .sort((a, b) => a.order - b.order) || [];

    if (remainingPages.length > 0 && group) {
      const originalSorted = [...group.pages].sort((a, b) => a.order - b.order);
      const deletedIndex = originalSorted.findIndex(p => p.id === pageId);
      const targetIndex = pickNeighborIndex(deletedIndex, remainingPages.length);
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId: remainingPages[targetIndex].id } });
    }
  }, [state.model, dispatch, deleteCanvas, dispatchGroupsUpdate]);

  const updatePage = useCallback((groupId: string, pageId: string, updates: Partial<PageAPI>) => {
    if (!state.model?.layout?.navigation?.groups) return;

    const newGroups = state.model.layout.navigation.groups.map(g =>
      g.id === groupId
        ? {
            ...g,
            pages: g.pages.map(p => (p.id === pageId ? { ...p, ...updates } : p)),
          }
        : g
    );

    dispatchGroupsUpdate(newGroups);
  }, [state.model, dispatchGroupsUpdate]);

  const reorderPages = useCallback((groupId: string, orderedIds: string[]) => {
    if (!state.model?.layout?.navigation?.groups) return;

    const group = state.model.layout.navigation.groups.find(g => g.id === groupId);
    if (!group) return;

    const newPages = orderedIds
      .map((id, index) => {
        const page = group.pages.find(p => p.id === id);
        return page ? { ...page, order: index } : null;
      })
      .filter((p): p is PageAPI => p !== null);

    const newGroups = state.model.layout.navigation.groups.map(g =>
      g.id === groupId ? { ...g, pages: newPages } : g
    );

    dispatchGroupsUpdate(newGroups);
  }, [state.model, dispatchGroupsUpdate]);

  // ============================================================================
  // Configuration
  // ============================================================================

  const updateConfiguration = useCallback((updates: Partial<NavigationConfigAPI>) => {
    if (!state.model?.layout?.navigation) return;

    const newModel = {
      ...state.model,
      layout: {
        ...state.model.layout,
        navigation: {
          ...state.model.layout.navigation,
          configuration: {
            ...state.model.layout.navigation.configuration,
            ...updates,
          },
        },
      },
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });
  }, [state.model, dispatch]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<NavigationContextValue>(
    () => ({
      groups,
      configuration,
      activeGroupId,
      activePageId,
      activeCanvasId,
      addGroup,
      removeGroup,
      updateGroup,
      reorderGroups,
      addPage,
      removePage,
      updatePage,
      reorderPages,
      updateConfiguration,
      getGroup,
      getPage,
      getPagesForGroup,
      setActivePage,
    }),
    [
      groups,
      configuration,
      activeGroupId,
      activePageId,
      activeCanvasId,
      addGroup,
      removeGroup,
      updateGroup,
      reorderGroups,
      addPage,
      removePage,
      updatePage,
      reorderPages,
      updateConfiguration,
      getGroup,
      getPage,
      getPagesForGroup,
      setActivePage,
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;
